// src/pages/Catalog.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Package, ShoppingBag } from 'lucide-react';
import { productLines } from '../data/productLines';
import { colors } from '../data/colors';
import { fetchItems } from '../api/itemsApi';
import { getUserByEssenNumber, incrementUserInteractions } from '../api/usersApi';
import { fetchCreditCardOptions } from '../api/creditCardOptionsApi';
import { getActivePromo } from '../api/promoConfigApi';
import { getShippingConfig } from '../api/shippingApi';
import ProductCard from '../components/ProductCard';
import ProductDetail from '../components/ProductDetail';
import ProductFilters from '../components/ProductFilters';
import PaymentModal from '../components/PaymentModal';
import ComboModal from '../components/ComboModal';
import Footer from '../components/Footer';
import LandingPage from './LandingPage';

// Default WhatsApp number if no valid user is found
const DEFAULT_WHATSAPP_NUMBER = '5492616815848';

const Catalog = () => {
    const { essenNumber } = useParams();

    const [whatsappNumber, setWhatsappNumber] = useState(DEFAULT_WHATSAPP_NUMBER);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLine, setSelectedLine] = useState('all');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [userNotFound, setUserNotFound] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showComboModal, setShowComboModal] = useState(false);
    const [selectedColor, setSelectedColor] = useState('all');
    const [creditOptions, setCreditOptions] = useState([]);
    const [cart, setCart] = useState([]);
    const [activePromo, setActivePromo] = useState(null);
    const [userConfig, setUserConfig] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState({
        type: 'efectivo',
        cardId: null,
        bank: null,
    });
    const [shippingConfig, setShippingConfig] = useState(null);

    // ---------------------------------------------------------------------
    // Data loading
    // ---------------------------------------------------------------------
    useEffect(() => {
        validateUserAndFetchProducts();
        loadCreditOptions();
        loadActivePromo();
        loadShippingConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [essenNumber]);

    const loadActivePromo = async () => {
        const promo = await getActivePromo();
        setActivePromo(promo);
    };

    const loadShippingConfig = async () => {
        try {
            const config = await getShippingConfig();
            setShippingConfig(config);
        } catch (error) {
            console.error('Error loading shipping config:', error);
        }
    };

    const validateUserAndFetchProducts = useCallback(async () => {
        try {
            setLoading(true);

            if (essenNumber) {
                const user = await getUserByEssenNumber(essenNumber);

                // Validate user exists and has an active plan
                const activePlans = ['Estándar', 'Premium'];
                const hasActivePlan = user && activePlans.includes(user.license);

                if (!hasActivePlan) {
                    console.warn(`Usuario ${essenNumber} no encontrado o plan inactivo. Mostrando la landing sin redirigir...`);
                    setUserNotFound(true);
                    setLoading(false);
                    return;
                }
                // User exists, update WhatsApp number if available
                if (user.phoneNumber) {
                    setWhatsappNumber(user.phoneNumber);
                }
                // Update user config (logo, accent color)
                setUserConfig({
                    logo: user.customLogo,
                    accentColor: user.accentColor
                });

                // Track interaction based on visit
                incrementUserInteractions(essenNumber).catch(err => console.error('Error tracking interaction:', err));
            }

            // Load product catalog
            const data = await fetchItems();
            let items = [];
            if (Array.isArray(data)) items = data;
            else if (data && Array.isArray(data.items)) items = data.items;
            else if (data && Array.isArray(data.data)) items = data.data;
            else if (data && typeof data === 'object') {
                const maybe = data.items || data.data || data.results;
                if (Array.isArray(maybe)) items = maybe;
            }

            // Custom sort order for product lines
            const sortOrder = [
                'Combos',
                'Contemporánea',
                'Contemporánea Cera Forte Terra',
                'Nuit',
                'Inducción',
                'Ediciones especiales',
                'Fusion',
                'Complementos',
                'Electro',
                'Bazar Premium',
                'Repuestos'
            ];

            items.sort((a, b) => {
                const lineA = (a.linea || '').toString();
                const lineB = (b.linea || '').toString();

                let indexA = sortOrder.indexOf(lineA);
                let indexB = sortOrder.indexOf(lineB);

                // If not found in the explicit list, put it at the end
                if (indexA === -1) indexA = 999;
                if (indexB === -1) indexB = 999;

                return indexA - indexB;
            });

            setProducts(items);
            setFilteredProducts(items.filter(item => item.active !== false));
        } catch (error) {
            console.error('Error cargando productos:', error);
            setProducts([]);
            setFilteredProducts([]);
        } finally {
            setLoading(false);
        }
    }, [essenNumber]);

    useEffect(() => {
        validateUserAndFetchProducts();
    }, [validateUserAndFetchProducts]);

    const loadCreditOptions = async () => {
        try {
            const options = await fetchCreditCardOptions();
            setCreditOptions(options);
        } catch (error) {
            console.error('Error loading credit options:', error);
            setCreditOptions([]);
        }
    };

    // ---------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------
    const normalize = (str = '') =>
        String(str)
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase();

    useEffect(() => {
        let filtered = products.filter(item => item.active !== false);
        if (searchTerm) {
            const nSearch = normalize(searchTerm);
            filtered = filtered.filter((p) => normalize(p.nombre).includes(nSearch));
        }
        if (selectedLine !== 'all') {
            filtered = filtered.filter((p) => {
                const itemLine = (p.linea || '').toString();
                if (selectedLine === 'Contemporánea Cera Forte Terra') {
                    const itemRevestimiento = p.revestimiento;
                    const hasCeramica = Array.isArray(itemRevestimiento)
                        ? itemRevestimiento.includes('Cerámica')
                        : itemRevestimiento === 'Cerámica';

                    return itemLine === selectedLine || hasCeramica;
                }
                return itemLine === selectedLine.toString();
            });
        }
        if (selectedColor !== 'all') {
            filtered = filtered.filter((p) => {
                const pc = p.color;
                if (Array.isArray(pc)) return pc.includes(selectedColor);
                if (pc == null) return false;
                return pc === selectedColor;
            });
        }
        setFilteredProducts(filtered);
    }, [searchTerm, selectedLine, selectedColor, products]);

    const isPromoActive = () => {
        if (!activePromo) return false;
        const now = new Date();
        const start = new Date(activePromo.startDate);
        const end = new Date(activePromo.endDate);
        return now >= start && now <= end;
    };

    // Price calculation – handles list price, cash/business price, and promotional discounts.
    const calculatePrice = (product) => {
        let price;
        if (paymentMethod.type === 'tarjeta') {
            price = product.list_price || product.precio;
        } else {
            // Cash payment (efectivo or cuotas_efectivo)
            const basePrice = product.business_price || product.precio;
            if (isPromoActive() && activePromo?.discountPercentage) {
                price = basePrice * (1 - (activePromo.discountPercentage / 100));
            } else {
                price = basePrice;
            }
        }

        // Apply individual product discount if active
        if (product.discount > 0 && product.discountEndDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // LocalDate from Java usually arrives as ISO YYYY-MM-DD
            // We append T00:00:00 to ensure it's treated as a local date in many browsers
            const endDate = new Date(product.discountEndDate + 'T00:00:00');
            endDate.setHours(0, 0, 0, 0);

            if (endDate >= today) {
                price = price * (1 - (product.discount / 100));
            }
        }

        return Math.round(price);
    };

    const calculateReintegro = (product) => {
        if (paymentMethod.type === 'tarjeta' && paymentMethod.cardId) {
            const selectedCard = creditOptions.find((c) => c.id === paymentMethod.cardId);
            if (selectedCard && selectedCard.discount > 0) {
                const price = calculatePrice(product);
                return price * (selectedCard.discount / 100);
            }
        }
        return 0;
    };

    const getAvailableInstallments = () => {
        if (paymentMethod.type === 'efectivo' || paymentMethod.type === 'cuotas_efectivo' || !paymentMethod.cardId) return [];
        const selectedCard = creditOptions.find((c) => c.id === paymentMethod.cardId);

        if (!selectedCard) return [];

        let installments = [...(selectedCard.installmentsNoInterest || [])];

        // Check for special rules based on day of week
        if (selectedCard.specialRules && Array.isArray(selectedCard.specialRules)) {
            // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
            const today = new Date().getDay();

            selectedCard.specialRules.forEach(rule => {
                if (rule.days && rule.days.includes(today)) {
                    if (rule.installments && Array.isArray(rule.installments)) {
                        installments = [...installments, ...rule.installments];
                    }
                }
            });
        }

        // Deduplicate and sort
        return [...new Set(installments)].sort((a, b) => a - b);
    };

    const resetPaymentMethod = () => {
        setPaymentMethod({ type: 'efectivo', cardId: null, bank: null });
    };

    const addToCart = (product) => {
        setCart([...cart, product]);
    };

    const removeFromCart = (index) => {
        const newCart = [...cart];
        newCart.splice(index, 1);
        setCart(newCart);
    };

    const getGradient = (color) => {
        const gradients = {
            terra: 'from-orange-400 via-red-400 to-pink-300',
            capri: 'from-blue-400 via-cyan-400 to-teal-300',
            cherry: 'from-red-500 via-pink-500 to-purple-400',
            nuit: 'from-indigo-500 via-purple-500 to-blue-400',
            rosa: 'from-pink-300 via-pink-400 to-pink-500',
        };
        return gradients[color] || 'from-purple-400 via-pink-400 to-orange-300';
    };

    // ---------------------------------------------------------------------
    // WhatsApp integration – includes reintegro information
    // ---------------------------------------------------------------------
    const sendWhatsApp = (product) => {
        const price = calculatePrice(product);
        const reintegro = calculateReintegro(product);
        let message = `Hola! Me interesa el producto: ${product.nombre}`;
        if (paymentMethod.type === 'efectivo') {
            message += '\nMedio de pago: Efectivo';
            if (isPromoActive()) message += ' (Precio Promocional)';
        } else if (paymentMethod.type === 'cuotas_efectivo') {
            message += '\nMedio de pago: Cuotas Efectivo';
            if (isPromoActive()) message += ' (Precio Promocional)';
        } else if (paymentMethod.type === 'tarjeta' && paymentMethod.cardId) {
            const selectedCard = creditOptions.find((c) => c.id === paymentMethod.cardId);
            if (selectedCard) {
                message += `\nMedio de pago: ${selectedCard.type} - ${selectedCard.bank}`;
            }
        }
        const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    // ---------------------------------------------------------------------
    // Rendering
    // ---------------------------------------------------------------------
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-600" />
            </div>
        );
    }

    // Numero de asesor inexistente o plan inactivo: mostramos la landing en la
    // misma URL (sin redirigir, para no generar el error "Pagina con
    // redireccion" en Search Console) y pedimos que no se indexe.
    if (userNotFound) {
        return (
            <>
                <LandingPage />
                <Helmet>
                    <meta name="robots" content="noindex, follow" />
                </Helmet>
            </>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>{`Catálogo Essen ${essenNumber} | Productos y Precios Actualizados`}</title>
                <meta name="description" content={`Catálogo completo de productos Essen del vendedor ${essenNumber}. Precios en efectivo y tarjeta, cuotas sin interés. Consultá por WhatsApp.`} />
                <link rel="canonical" href={`https://miessen.shop/${essenNumber}`} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://miessen.shop/${essenNumber}`} />
                <meta property="og:title" content={`Catálogo Essen ${essenNumber}`} />
                <meta property="og:description" content="Productos Essen con precios actualizados y cuotas sin interés" />
                <meta property="og:image" content="https://miessen.shop/og-image.jpg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content="Mi Essen" />
                <meta property="og:locale" content="es_AR" />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="alternate" hreflang="es-AR" href={`https://miessen.shop/${essenNumber}`} />
                <script type="application/ld+json">
                    {`
                        {
                            "@context": "https://schema.org",
                            "@type": "Store",
                            "name": "Catálogo Essen de Asesor #${essenNumber}",
                            "description": "Catálogo digital de productos Essen con precios y cuotas sin interés.",
                            "url": "https://miessen.shop/${essenNumber}",
                            "priceRange": "$$",
                            "address": {
                                "@type": "PostalAddress",
                                "addressCountry": "AR"
                            }
                        }
                    `}
                </script>
            </Helmet>
            {/* Header */}
            <div className="bg-white shadow-sm md:top-0 z-40">
                <div className="max-w-7xl mx-auto px-1 py-1">
                    <div className="flex items-center justify-center gap-3">
                        {userConfig?.logo ? (
                            <img
                                src={userConfig.logo}
                                alt="Logo Personal"
                                className="h-24 object-contain py-2"
                            />
                        ) : (
                            <>
                                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-2 rounded-lg">
                                    <Package className="w-6 h-6 text-white" />
                                </div>
                                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                    Mi Essen - Catálogo
                                </h1>
                            </>
                        )}
                    </div>
                    <ProductFilters
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        selectedLine={selectedLine}
                        setSelectedLine={setSelectedLine}
                        productLines={productLines}
                        setShowPaymentModal={setShowPaymentModal}
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod}
                        selectedColor={selectedColor}
                        setSelectedColor={setSelectedColor}
                        colors={colors}
                        creditOptions={creditOptions}
                        accentColor={userConfig?.accentColor}
                    />
                </div>
            </div>

            {/* Product Grid */}
            <div className="max-w-7xl mx-auto px-4 py-8 pb-24">
                {filteredProducts.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-gray-500 text-lg">No se encontraron productos</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                        {filteredProducts.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                colors={colors}
                                setSelectedProduct={setSelectedProduct}
                                sendWhatsApp={sendWhatsApp}
                                accentColor={userConfig?.accentColor}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Floating Cart Button */}
            <button
                onClick={() => setShowComboModal(true)}
                className={`fixed bottom-6 left-6 z-40 text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-3 hover:scale-105 transition-transform ${!userConfig?.accentColor ? 'bg-gray-900' : ''}`}
                style={userConfig?.accentColor ? { backgroundColor: userConfig.accentColor } : {}}
            >
                <div className="relative">
                    <ShoppingBag className="w-6 h-6" />
                    {cart.length > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                            {cart.length}
                        </span>
                    )}
                </div>
                <span className="font-bold pr-2">Ver Combo</span>
            </button>

            {/* Modals */}
            {selectedProduct && (
                <ProductDetail
                    product={selectedProduct}
                    colors={colors}
                    onClose={() => setSelectedProduct(null)}
                    whatsappNumber={whatsappNumber}
                    calculatePrice={calculatePrice}
                    getAvailableInstallments={getAvailableInstallments}
                    paymentMethod={paymentMethod}
                    addToCart={addToCart}
                    creditOptions={creditOptions}
                    accentColor={userConfig?.accentColor}
                    sendWhatsApp={sendWhatsApp}
                    shippingConfig={shippingConfig}
                    activePromo={activePromo}
                />
            )}

            {showPaymentModal && (
                <PaymentModal
                    onClose={() => setShowPaymentModal(false)}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    creditOptions={creditOptions}
                    resetPaymentMethod={resetPaymentMethod}
                    accentColor={userConfig?.accentColor}
                />
            )}

            {showComboModal && (
                <ComboModal
                    cart={cart}
                    removeFromCart={removeFromCart}
                    onClose={() => setShowComboModal(false)}
                    paymentMethod={paymentMethod}
                    creditOptions={creditOptions}
                    whatsappNumber={whatsappNumber}
                    calculatePrice={calculatePrice}
                    isPromoActive={isPromoActive}
                    calculateReintegro={calculateReintegro}
                    accentColor={userConfig?.accentColor}
                    shippingConfig={shippingConfig}
                    essenNumber={essenNumber}
                />
            )}

            <Footer />
        </div>
    );
};

export default Catalog;
